import { useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { Address } from "viem"
import { useAccount, useConfig } from "wagmi"

import { Button } from "@/components/button"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { DisplayLevel } from "@/domains/common/components/display-level"
import { ArrowDownCappedIcon } from "@/domains/icons"
import { CoinsIcon } from "@/domains/icons/huge-icons/monotone/coins"
import { VoteIcon } from "@/domains/icons/huge-icons/monotone/vote"
import { claimAllNftBribesInteraction } from "@/domains/interactions/claim-all-nft-bribes/claim-all-nft-bribes-interaction"
import { cn, dollarsPrettified, formatCurrency, formatPercentage } from "@/domains/library"
import { ClaimEverythingByNftFormat } from "@/domains/lock-rewards/strategies/selection/lock-reward-selection-strategy"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"
import { SCALE } from "@/domains/tokens/static-tokens"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useVestedToken } from "@/hooks/use-vested-token"
import useEventEmitter from "@/hooks/useEventEmitter"

export const VotingRewardsSummaryCard = observer(() => {
  const account = useAccount()
  const chainConfig = useChainConfig()
  const config = useConfig()
  const emitter = useEventEmitter()
  const scaleApp = useScaleApplication()
  const vestedToken = useVestedToken()
  const [isClaiming, setIsClaiming] = useState(false)

  // Get all locks (NFTs)
  const locks = scaleApp.Lock.Repository.getAll()
  const veTotalSupply = scaleApp.Stats.veTotalSupply

  // Calculate lock stats (same as UserLockStats)
  const largestNftValue = useMemo(() => {
    const size = Math.max(...locks.map((nft) => Number(nft.lockValue)))
    if (size === -Infinity) {
      return 0
    }
    return size
  }, [locks])

  const userLocked = useMemo(() => {
    const locked = locks.reduce((acc, nft) => acc + Number(nft.lockAmount), 0)
    return locked
  }, [locks])

  const userVePower = useMemo(() => {
    const power = locks.reduce((acc, nft) => acc + Number(nft.lockValue), 0)
    return power
  }, [locks])

  const userPower = userLocked > 0 ? userVePower / userLocked : 0
  const userInfluence = veTotalSupply > 0 ? Number(userLocked) / Number(veTotalSupply) : 0

  // Calculate total value (locked SCALE * SCALE price)
  const totalValue = userLocked * SCALE.bestPrice

  // Get voting rewards
  const votingRewardsTotalUSD = scaleApp.LockRewards.Repository.totalRewardsValueUSD
  const hasRewards = votingRewardsTotalUSD > 0
  const hasLocks = locks.length > 0

  const handleClaimAll = async (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click from scrolling
    if (!hasRewards || isClaiming) return

    setIsClaiming(true)
    try {
      if (!account.address) {
        throw new Error("no address found")
      }

      const allTokens = scaleApp.LockRewards.Repository.getAll()
      const selectionStrategy = scaleApp.LockRewards.Strategies.NftSelection
      const claimFormat = selectionStrategy.toClaimFormat(allTokens)

      for (let i = 0; i < claimFormat.length; i++) {
        const item = claimFormat[i] as ClaimEverythingByNftFormat
        if (!item) continue

        const result = await claimAllNftBribesInteraction({
          account: account.address,
          chainId: chainConfig.chainId,
          config,
          voterAddress: chainConfig.equalizer.contracts.voter,
          bribes: item.bribes as `0x${string}`[],
          bribeTokens: item.bribeTokens as `0x${string}`[][],
          nft: { id: item.nftId },
          emitter,
        })

        if (result === false) {
          break
        }
      }
    } catch (error) {
      console.error("VotingRewardsSummaryCard→handleClaimAll error", error)
    } finally {
      setIsClaiming(false)
    }
  }

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click from scrolling
    const element = document.getElementById("voting-rewards")
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  const handleCardClick = () => {
    const element = document.getElementById("voting-rewards")
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        "group relative flex w-full flex-col overflow-hidden rounded-3xl border border-p400-light bg-p300 shadow-2xl transition-all",
        "hover:border-p400-light/80",
      )}
    >
      {/* Header */}
      <div className="relative z-10 p-6 pb-4">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-t300/20 bg-p400/10 text-t300">
              <img src="/scale/white_logo.svg" alt="SCALE" className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-lg font-bold leading-tight text-t100">veSCALE</h3>
              <p className="text-xs font-medium text-t300">Overview</p>
            </div>
          </div>

          {/* Level Badge */}
          {hasLocks && <DisplayLevel lockValue={largestNftValue} />}
        </div>

        {/* Hero Metrics Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase text-t300">Total Value</p>
            <div className="text-2xl font-bold tracking-tight text-t100">{dollarsPrettified(totalValue, 2)}</div>
          </div>
          <div className="text-right">
            <p className="mb-1 text-[10px] font-bold uppercase text-t300">Unclaimed Rewards</p>
            <div className="text-2xl font-bold tracking-tight text-t300">
              {dollarsPrettified(votingRewardsTotalUSD, 2)}
            </div>
            <div className="mt-0.5 flex items-center justify-end gap-1.5 text-[10px] font-medium text-t300">
              <span>&nbsp;</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 border-y border-t300/[0.12] bg-t300/[0.02]">
        {/* Positions Count */}
        <div className="flex flex-col items-center justify-center border-r border-t300/[0.12] p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Influence</span>
          <span className="flex items-center gap-1 text-sm font-bold text-t100">
            {formatPercentage(userInfluence, 2)}
          </span>
        </div>

        {/* Top APR */}
        <div className="flex flex-col items-center justify-center border-r border-t300/[0.12] p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Power</span>
          <span className="flex items-center gap-1 text-sm font-bold text-t100">{formatPercentage(userPower, 2)}</span>
        </div>

        {/* Status Signal */}
        <div className="flex flex-col items-center justify-center p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Locked</span>
          <span className="flex items-center gap-1 text-sm font-bold text-t100">
            {formatCurrency(userLocked, 0)}
            <DisplayTokens tokens={[SCALE]} tokenSize={14} />
          </span>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="z-10 mt-auto flex gap-3 bg-p300 p-4">
        {/* Secondary Action: View Details */}
        <Button
          variant="percentage"
          size="sm"
          onClick={handleViewDetails}
          className="text-xs font-bold uppercase tracking-wide"
        >
          Details <ArrowDownCappedIcon className="h-3.5 w-3.5" />
        </Button>

        {/* Primary Action: Claim All */}
        <Button
          variant={hasRewards ? "primary" : "percentage"}
          size="sm"
          onClick={handleClaimAll}
          disabled={!hasRewards || isClaiming}
          className="flex-1 font-bold"
        >
          {isClaiming ? (
            <>
              <span className="animate-spin">⟳</span> Claiming...
            </>
          ) : (
            <>
              <CoinsIcon className="h-4 w-4" /> Claim All
            </>
          )}
        </Button>
      </div>
    </div>
  )
})
